import { streamChatSse } from "@/zapi-frontend/shared/api/chatSse";
import { useEffect, useRef, useState } from "react";
import { XIcon } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { AiMessage, ChatShell, TypingDots, UserMessage } from "@/zapi-frontend/shared/ui/chat";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { chatPanelWidthClasses } from "@/zapi-frontend/shared/ui/ResponsiveChatPanel";
import { MessageMarkdown } from "@/zapi-frontend/shared/ui/custom/ChatMessage/MessageMarkdown";
import { LeoWelcome } from "@/zapi-frontend/widgets/LeoChat/ui/LeoWelcome";
import { API_URL } from "@/zapi-frontend/shared/api/axios";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { useProjectsListChatStore, type TProjectsListChatMessage } from "@/zapi-frontend/stores/projectsListChatStore";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { ChatSelectionFlagAffordance } from "@/zapi-frontend/features/FlagDeficiency/ui/ChatSelectionFlagAffordance";
import type { TPortfolioChatScope } from "../../lib/portfolioChatFilterScope";
import { useTranslation } from "react-i18next";

type TProps = {
  isOverlay?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  filterScope?: TPortfolioChatScope | null;
};

const EMPTY_MESSAGES: TProjectsListChatMessage[] = [];
const MAX_REQUEST_MESSAGES = 40;

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function fixProjectLinks(content: string, validIds?: Set<number> | null): string {
  let out = content;
  const marker = "](/projects/";
  let idx = out.indexOf(marker);
  while (idx !== -1) {
    const closeParen = out.indexOf(")", idx + 2);
    if (closeParen === -1) break;
    const href = out.slice(idx + 2, closeParen);
    const linkedId = /^\/projects\/(\d+)$/.exec(href)?.[1];
    const isProject = linkedId !== undefined && (!validIds || validIds.has(Number(linkedId)));
    const isDraft =
      /^\/projects\/draft-[\w-]+$/.test(href) ||
      (linkedId !== undefined && !!validIds && !validIds.has(Number(linkedId)));
    if (!isProject && !isDraft) {
      idx = out.indexOf(marker, idx + 1);
      continue;
    }
    let depth = 1;
    let start = -1;
    for (let i = idx - 1; i >= 0; i--) {
      const ch = out[i];
      if (ch === "]") depth++;
      else if (ch === "[") {
        depth--;
        if (depth === 0) {
          start = i;
          break;
        }
      }
    }
    if (start === -1) {
      idx = out.indexOf(marker, idx + 1);
      continue;
    }
    const text = out.slice(start + 1, idx).replace(/\\([[\]])/g, "$1");
    const draftText = isDraft && !/\(draft\)\s*$/i.test(text) ? `${text} (draft)` : text;
    const anchor = isDraft ? `**${escapeHtml(draftText)}**` : `<a href="${href}">${escapeHtml(text)}</a>`;
    out = out.slice(0, start) + anchor + out.slice(closeParen + 1);
    idx = out.indexOf(marker, start + anchor.length);
  }
  return out;
}

type TSSEEvent =
  | { type: "start" }
  | { type: "meta"; validProjectIds?: number[] }
  | { type: "content"; content: string }
  | { type: "replace"; content: string }
  | { type: "finish" };

async function streamProjectsListChat(
  messagesHistory: TProjectsListChatMessage[],
  organizationId: number | null,
  filterScope: TPortfolioChatScope | null,
  abortSignal: AbortSignal,
  onContentChunk: (accumulated: string) => void,
  onMeta?: (validIds: Set<number>) => void
): Promise<string> {
  let accumulated = "";

  await streamChatSse<TSSEEvent>({
    url: `${API_URL}/chat/projects-list/ask`,
    body: { messagesHistory, organizationId: organizationId ?? undefined, filterScope: filterScope ?? undefined },
    signal: abortSignal,
    onEvent: (event) => {
      if (event.type === "meta" && Array.isArray(event.validProjectIds)) {
        onMeta?.(new Set(event.validProjectIds));
      }
      if (event.type === "content") {
        accumulated += event.content;
        onContentChunk(accumulated);
      }
      if (event.type === "replace") {
        accumulated = event.content;
        onContentChunk(accumulated);
      }
    }
  });

  return accumulated;
}

export function ChatPanel({ isOverlay = false, isOpen = false, onClose, filterScope = null }: TProps) {
  const { t } = useTranslation(["projects", "projectHome", "common"]);
  const currentOrganizationId = useOrganizationStore((s) => s.currentOrganizationId);
  const isFlagEnabled = useFeatureFlag("case-brief-flag");
  const messages = useProjectsListChatStore(
    (s) => (currentOrganizationId ? s.historyByOrg[currentOrganizationId] : undefined) ?? EMPTY_MESSAGES
  );
  const setOrgMessages = useProjectsListChatStore((s) => s.setMessages);
  const [input, setInput] = useState("");
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const validIdsRef = useRef<Set<number> | null>(null);

  useEffect(() => {
    abortRef.current?.abort();
  }, [currentOrganizationId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streamingText]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const send = async (suggestion?: string) => {
    const text = (suggestion ?? input).trim();
    if (!text || isStreaming || !currentOrganizationId) return;
    const orgId = currentOrganizationId;

    const history: TProjectsListChatMessage[] = [...messages, { role: "user", content: text }];
    setOrgMessages(orgId, history);
    setInput("");
    setIsStreaming(true);
    setStreamingText("");

    const abort = new AbortController();
    abortRef.current = abort;

    try {
      const answer = await streamProjectsListChat(
        history.slice(-MAX_REQUEST_MESSAGES),
        orgId,
        filterScope,
        abort.signal,
        setStreamingText,
        (ids) => {
          validIdsRef.current = new Set([...(validIdsRef.current ?? []), ...ids]);
        }
      );
      setOrgMessages(orgId, [
        ...history,
        { role: "assistant", content: answer || t("projects:chat.noResponseFallback") }
      ]);
    } catch (e) {
      if (!abort.signal.aborted) {
        setOrgMessages(orgId, [...history, { role: "assistant", content: t("projects:chat.genericErrorFallback") }]);
      }
    } finally {
      setIsStreaming(false);
      setStreamingText(null);
    }
  };

  const clearChat = () => {
    abortRef.current?.abort();
    setIsStreaming(false);
    setStreamingText(null);
    validIdsRef.current = null;
    if (currentOrganizationId) setOrgMessages(currentOrganizationId, []);
  };

  const canClear = messages.length > 0 || isStreaming;

  return (
    <ChatShell
      data-testid="projects-chat-panel"
      title={t("projectHome:leo.title")}
      appearance="leo"
      placeholder={t("projectHome:leo.placeholderAnything")}
      footnote={t("projectHome:leo.disclaimer")}
      busyNotice={t("projectHome:leo.busy")}
      submitLabel={t("projectHome:leo.send")}
      optionsMenuLabel={t("projects:chat.optionsMenuLabel")}
      stopLabel={t("projects:chat.stop")}
      clearConfirm={{
        menuLabel: t("common:clear"),
        title: t("projectHome:leo.clearTitle"),
        cancelText: t("projectHome:leo.cancel"),
        confirmText: t("common:clear"),
        toastText: t("projectHome:leo.chatCleared")
      }}
      inputTestId="projects-chat-panel-input"
      sendTestId="projects-chat-panel-send-button"
      clearTestId="projects-chat-panel-clear-button"
      className={cn(
        isOverlay
          ? cn(
              "absolute inset-y-0 right-0 z-30 w-full mobile:max-w-[364px] border-l border-innoveraZinc-100 transition-transform duration-300 ease-in-out",
              isOpen
                ? "translate-x-0 before:pointer-events-none before:absolute before:inset-y-0 before:right-full before:w-[23px] before:bg-linear-to-l before:from-black/5 before:to-transparent before:content-['']"
                : "pointer-events-none translate-x-full"
            )
          : cn(chatPanelWidthClasses, "overflow-hidden rounded-xl border", !isOpen && "hidden")
      )}
      headerEnd={
        onClose ? (
          <Button
            variant="ghost"
            size="icon-sm"
            shape="pill"
            data-testid="projects-chat-panel-close-button"
            onClick={onClose}
            aria-label={t("projectHome:leo.hide")}
            title={t("projectHome:leo.hide")}
          >
            <XIcon />
          </Button>
        ) : undefined
      }
      scrollRef={scrollRef}
      input={input}
      onInputChange={setInput}
      onSubmit={() => void send()}
      isBusy={isStreaming}
      onClear={clearChat}
      canClear={canClear}
    >
      {messages.length === 0 && !isStreaming && (
        <LeoWelcome
          title={t("projectHome:leo.welcomeTitle")}
          subtitle={t("projectHome:leo.welcomeSubtitleProjects")}
          suggestions={[t("projectHome:leo.suggestionActive"), t("projectHome:leo.suggestionAttention")]}
          onPick={(suggestion) => void send(suggestion)}
        />
      )}

      {messages.map((m, i) => {
        if (m.role === "user") {
          return (
            <UserMessage key={i}>
              <span className="whitespace-pre-wrap">{m.content}</span>
            </UserMessage>
          );
        }
        return (
          <AiMessage key={i} className="w-full">
            <div
              data-flaggable-answer
              onClickCapture={(e) => {
                const link = (e.target as HTMLElement).closest("a");
                if (!link) return;
                e.preventDefault();
                e.stopPropagation();
                window.open(link.getAttribute("href") ?? "", "_blank", "noopener");
              }}
            >
              <MessageMarkdown content={fixProjectLinks(m.content, validIdsRef.current)} />
            </div>
          </AiMessage>
        );
      })}

      {streamingText !== null && (
        <AiMessage className="w-full">
          {streamingText === "" ? (
            <TypingDots />
          ) : (
            <div data-flaggable-answer>
              <MessageMarkdown content={fixProjectLinks(streamingText, validIdsRef.current)} />
            </div>
          )}
        </AiMessage>
      )}

      <ChatSelectionFlagAffordance enabled={Boolean(isFlagEnabled)} organizationId={currentOrganizationId} />
    </ChatShell>
  );
}
